import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SagraCard from "@/components/SagraCard";
import CookieBanner from "@/components/CookieBanner";
import { Search, MapPin, ArrowRight, Navigation, Calendar, Newspaper, ChevronLeft, ChevronRight } from "lucide-react";
import { isWithinInterval, parseISO, format } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";
import homepageBg from '../img/homepage.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [regioni, setRegioni] = useState([]);
  const [regioniCounts, setRegioniCounts] = useState({});
  const [allSagre, setAllSagre] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegione, setSelectedRegione] = useState("");

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    try {
      await axios.post(`${API}/seed`);
      
      const [regioniRes, sagreRes, newsRes, countsRes] = await Promise.all([
        axios.get(`${API}/regioni`),
        axios.get(`${API}/sagre`),
        axios.get(`${API}/news`),
        axios.get(`${API}/regioni/counts`)
      ]);
      
      setRegioni(regioniRes.data.regioni);
      setAllSagre(sagreRes.data);
      setNews(newsRes.data.slice(0, 3)); // Get only first 3 news
      setRegioniCounts(countsRes.data.counts);
    } catch (error) {
      console.error("Error initializing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatNewsDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMM yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  // Filter sagre in corso (happening now)
  const sagreInCorso = allSagre.filter(sagra => {
    try {
      const today = new Date();
      const start = parseISO(sagra.data_inizio);
      const end = parseISO(sagra.data_fine);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return isWithinInterval(today, { start, end });
    } catch {
      return false;
    }
  });

  // Featured sagre (upcoming, not in corso)
  const featuredSagre = allSagre
    .filter(sagra => {
      try {
        const today = new Date();
        const start = parseISO(sagra.data_inizio);
        return start > today;
      } catch {
        return false;
      }
    })
    .slice(0, 8);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedRegione) params.set("regione", selectedRegione);
    navigate(`/cerca?${params.toString()}`);
  };

  const handleNearby = () => {
    navigate("/cerca?geo=true");
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - starts from top, navbar overlays it */}
      <section className="relative h-[100vh] min-h-[700px] flex items-center justify-center -mt-14">
        <div className="absolute inset-0">
          <img
            src={homepageBg}
            alt="Italian cuisine"
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-4 text-center pt-14">
          <div className="flex justify-center mb-6 animate-fade-in">
            <img 
              src="/icons/Logo_Sagre.svg" 
              alt="Sagre Oggi" 
              className="h-16 md:h-20 w-auto brightness-0 invert"
            />
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-white mb-6 animate-slide-up leading-tight">
            Scopri le tradizioni
            <br />
            <span className="text-white/90">gastronomiche italiane</span>
          </h1>
          
          <p className="text-white/70 text-lg mb-10 animate-slide-up stagger-1 max-w-md mx-auto">
            Trova sagre, feste paesane e sapori autentici vicino a te
          </p>

          {/* Search Box */}
          <div className="animate-slide-up stagger-2">
            <div className="bg-white rounded-2xl p-2 shadow-elevated max-w-lg mx-auto">
              <div className="flex gap-2">
                <Select value={selectedRegione} onValueChange={setSelectedRegione}>
                  <SelectTrigger 
                    className="flex-1 h-12 rounded-xl border-0 bg-muted/50 text-left"
                    data-testid="hero-regione-select"
                  >
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <SelectValue placeholder="Dove vuoi cercare?" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {regioni.map((reg) => (
                      <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleSearch}
                  size="icon"
                  className="h-12 w-12 rounded-xl btn-subtle"
                  data-testid="hero-search-btn"
                >
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <button
              onClick={handleNearby}
              className="mt-4 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors"
              data-testid="nearby-search-btn"
            >
              <Navigation className="w-4 h-4" />
              Usa la mia posizione
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Sagre In Corso Section */}
      {sagreInCorso.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-green-50 to-background" data-testid="live-section">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <h2 className="text-xl md:text-2xl text-foreground">
                  In corso ora
                </h2>
              </div>
              <span className="text-sm text-muted-foreground">
                {sagreInCorso.length} sagr{sagreInCorso.length === 1 ? 'a' : 'e'} attiv{sagreInCorso.length === 1 ? 'a' : 'e'}
              </span>
            </div>

            {/* Horizontal scroll on mobile, grid on desktop */}
            <div className="flex gap-4 overflow-x-auto pb-4 md:grid md:grid-cols-2 lg:grid-cols-4 md:overflow-visible md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
              {sagreInCorso.slice(0, 4).map((sagra, index) => (
                <div key={sagra.id} className="min-w-[280px] md:min-w-0">
                  <SagraCard sagra={sagra} index={index} />
                </div>
              ))}
            </div>

            {sagreInCorso.length > 4 && (
              <div className="mt-4 text-center">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/cerca')}
                  className="rounded-xl"
                >
                  Vedi tutte le {sagreInCorso.length} sagre in corso
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

{/* Regions Quick Access Section */}
      <section className="py-12 bg-muted/30" data-testid="regions-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-primary text-sm font-medium mb-2">Esplora per Regione</p>
            <h2 className="text-2xl md:text-3xl text-foreground">
              Sagre in tutta Italia
            </h2>
          </div>
          
          {/* Horizontal scrollable region buttons with arrows */}
          <div className="relative group">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('regions-scroll');
                container.scrollBy({ left: -300, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50 hidden md:block"
              aria-label="Scorri a sinistra"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>

            {/* Scrollable container */}
            <div 
              id="regions-scroll"
              className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {regioni.map((regione) => {
                const count = regioniCounts[regione] || 0;
                return (
                  <button
                    key={regione}
                    onClick={() => navigate(`/cerca?regione=${encodeURIComponent(regione)}`)}
                    className="flex-shrink-0 group/item relative bg-card border border-border rounded-xl px-4 py-3 hover:border-primary/50 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="font-medium text-foreground whitespace-nowrap">
                        {regione}
                      </span>
                      <Badge 
                        variant={count > 0 ? "default" : "secondary"} 
                        className="rounded-full text-xs min-w-[24px] justify-center"
                      >
                        {count}
                      </Badge>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('regions-scroll');
                container.scrollBy({ left: 300, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50 hidden md:block"
              aria-label="Scorri a destra"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
            
            {/* Gradient fade indicators */}
            <div className="hidden md:block absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-muted/30 to-transparent pointer-events-none" />
            <div className="hidden md:block absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-muted/30 to-transparent pointer-events-none" />
          </div>
          
          {/* Mobile hint */}
          <p className="text-center text-xs text-muted-foreground mt-2 md:hidden flex items-center justify-center gap-2">
            <ChevronLeft className="w-3 h-3" />
            Scorri per vedere tutte le regioni
            <ChevronRight className="w-3 h-3" />
          </p>
        </div>
      </section>

      {/* Featured Section */}
      <section className="py-16 px-4" data-testid="featured-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-primary text-sm font-medium mb-2">Prossimamente</p>
              <h2 className="text-3xl md:text-4xl text-foreground">
                Sagre in arrivo
              </h2>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/cerca')}
              className="hidden sm:flex items-center gap-1 text-muted-foreground hover:text-foreground"
              data-testid="view-all-btn"
            >
              Vedi tutte
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton-gentle">
                  <div className="aspect-[3/2] bg-muted rounded-xl mb-3" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredSagre.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredSagre.map((sagra, index) => (
                <SagraCard key={sagra.id} sagra={sagra} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nessuna sagra in programma al momento.</p>
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Button 
              variant="outline" 
              onClick={() => navigate('/cerca')}
              className="rounded-xl"
            >
              Vedi tutte le sagre
            </Button>
          </div>
        </div>
      </section>

      {/* News Section */}
      {news.length > 0 && (
        <section className="py-16 bg-muted/30" data-testid="news-section">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl text-foreground mb-2 flex items-center gap-2">
                  <Newspaper className="w-7 h-7 text-primary" />
                  News e Novità
                </h2>
                <p className="text-muted-foreground">
                  Le ultime notizie dal mondo delle sagre
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/news')}
                className="rounded-xl hidden sm:flex gap-2"
              >
                Vai al blog
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/news/${item.id}`}
                  className="group"
                >
                  <Card className="h-full overflow-hidden border-0 shadow-subtle hover:shadow-md transition-shadow">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={item.immagine_url || "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400"}
                        alt={item.titolo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" className="rounded-md text-xs">
                          {item.categoria || "News"}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatNewsDate(item.data_pubblicazione)}
                        </span>
                      </div>
                      
                      <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {item.titolo}
                      </h3>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.contenuto}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Button 
                variant="outline" 
                onClick={() => navigate('/news')}
                className="rounded-xl"
              >
                Vai al blog
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#d87b64' }} data-testid="cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl text-white font-semibold mb-4">
            Organizzi una sagra?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Aggiungi il tuo evento gratuitamente e raggiungi migliaia di appassionati.
          </p>
          <Button 
            onClick={() => navigate('/aggiungi')}
            size="lg" 
            className="rounded-xl h-12 px-8 bg-white text-[#d87b64] hover:bg-white/90 hover:text-[#d87b64] font-semibold shadow-lg"
            data-testid="cta-add-btn"
          >
            Aggiungi la tua sagra
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 border-t border-border bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/icons/Logo_Sagre.svg" 
                alt="Sagre Oggi" 
                className="h-6 w-auto"
              />
            </Link>
            <p className="text-xs text-muted-foreground">
              © 2025 Sagre Italia
            </p>
          </div>
          <p className="text-[10px] text-muted-foreground/70 text-center leading-relaxed">
            Su SAGRE-OGGI.it trovi una selezione di eventi basata su segnalazioni e ricerche online. 
            Poiché i programmi possono subire modifiche dell'ultimo minuto indipendenti dalla nostra volontà, 
            decliniamo ogni responsabilità su variazioni di date e servizi.
          </p>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
};

export default HomePage;